import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../store/authStore';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { Sparkles, Loader2, ArrowUp, ArrowRight, ArrowDown } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ScoreRing = ({ score }) => {
  const radius = 54;
  const stroke = 8;
  const circumference = 2 * Math.PI * radius;
  const progress = score != null ? (score / 100) * circumference : 0;
  const color = score >= 70 ? '#22c55e' : score >= 40 ? '#f59e0b' : '#ef4444';

  return (
    <div className="relative inline-flex items-center justify-center">
      <svg width="128" height="128" viewBox="0 0 128 128">
        <circle cx="64" cy="64" r={radius} fill="none" stroke="#E2E8F0" strokeWidth={stroke} />
        <circle
          cx="64" cy="64" r={radius} fill="none"
          stroke={color} strokeWidth={stroke}
          strokeDasharray={circumference}
          strokeDashoffset={circumference - progress}
          strokeLinecap="round"
          transform="rotate(-90 64 64)"
          className="transition-all duration-1000 ease-out"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-3xl font-black" style={{ fontFamily: 'Manrope, sans-serif', color }}>
          {score ?? '—'}
        </span>
        <span className="text-xs text-[#475569]">/ 100</span>
      </div>
    </div>
  );
};

const impactIcon = {
  high: <ArrowUp className="h-3.5 w-3.5 text-red-500" />,
  medium: <ArrowRight className="h-3.5 w-3.5 text-amber-500" />,
  low: <ArrowDown className="h-3.5 w-3.5 text-green-500" />,
};

const impactBadge = {
  high: 'bg-red-100 text-red-700',
  medium: 'bg-amber-100 text-amber-700',
  low: 'bg-green-100 text-green-700',
};

export const FormHealthScore = ({ qrId }) => {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const analyze = async () => {
    setLoading(true);
    setData(null);
    try {
      const res = await axios.get(`${API}/qr-codes/${qrId}/health-score`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 30000,
      });
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate health score');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="p-6 border border-[#E2E8F0]" data-testid="health-score-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-[#1d4ed8]" />
          <h2 className="text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
            AI Form Health Score
          </h2>
        </div>
        <Button
          data-testid="analyze-health-btn"
          onClick={analyze}
          disabled={loading}
          className="rounded-full bg-[#1d4ed8] hover:bg-[#1e40af]"
          size="sm"
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Analyzing...
            </>
          ) : (
            <>
              <Sparkles className="h-4 w-4 mr-2" />
              {data ? 'Re-analyze' : 'Analyze'}
            </>
          )}
        </Button>
      </div>

      {!data && !loading && (
        <p className="text-sm text-[#475569]">
          Click "Analyze" to let AI evaluate your form's drop-off patterns and suggest improvements.
        </p>
      )}

      {loading && (
        <div className="flex flex-col items-center justify-center py-10 gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-[#1d4ed8]" />
          <p className="text-sm text-[#475569]">AI is analyzing your form...</p>
        </div>
      )}

      {data && !loading && (
        <div className="space-y-6">
          {/* Score + Summary */}
          <div className="flex items-center gap-6">
            <ScoreRing score={data.score} />
            <div className="flex-1">
              <p className="text-sm text-[#475569] mb-1">
                {data.total_sessions} sessions &middot; {data.total_completed} completed
              </p>
              <p className="text-sm leading-relaxed" data-testid="health-summary">
                {data.summary}
              </p>
            </div>
          </div>

          {/* Suggestions */}
          {data.suggestions && data.suggestions.length > 0 ? (
            <div>
              <h3 className="font-bold text-sm mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Suggestions ({data.suggestions.length})
              </h3>
              <div className="space-y-3">
                {data.suggestions.map((s, i) => (
                  <div
                    key={`item-${i}`}
                    className="p-3 rounded-lg border border-[#E2E8F0] bg-[#F8FAFC]"
                    data-testid={`suggestion-${i}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${impactBadge[s.impact] || impactBadge.low}`}>
                        {impactIcon[s.impact] || impactIcon.low}
                        {s.impact}
                      </span>
                      <span className="text-xs text-[#475569]">Q{s.question_index}</span>
                    </div>
                    <p className="text-sm font-medium mb-1">{s.issue}</p>
                    <p className="text-sm text-[#475569]">{s.suggestion}</p>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            data.score != null && (
              <p className="text-sm text-green-700 bg-green-50 p-3 rounded-lg border border-green-200">
                Your form looks great! No critical issues detected.
              </p>
            )
          )}
        </div>
      )}
    </Card>
  );
};
